import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { ApiGet, ApiPut } from '../ApiServices/ApiServices';
import { toast } from 'react-toastify';
import { Capacitor } from '@capacitor/core';
import kitchenAlertService from '../services/kitchenAlertService';

const OrderAlertContext = createContext();

export const useOrderAlert = () => {
  const context = useContext(OrderAlertContext);
  if (!context) {
    return {
      pendingOrders: [],
      activeItemsByTable: {},
      isRinging: false,
      acceptOrder: () => {},
      rejectOrder: () => {},
      actionLoading: null
    };
  }
  return context;
};

// Roles that should receive order alerts
const ALERT_ROLES = ['restaurant', 'kitchen', 'cashier', 'captain', 'admin', 'supadmin', 'branch'];

// Role-specific API configuration
const getRoleConfig = (role) => {
  switch (role) {
    case 'restaurant':
      return {
        fetchEndpoint: '/api/restaurant/orders/filter',
        updateEndpoint: '/api/restaurant/orders/update',
        acceptStatus: 'CONFIRMED',
        rejectStatus: 'CANCELLED',
        fetchParams: { status: 'PENDING', pageSize: 50, pageNumber: 0 }
      };
    case 'kitchen':
      return {
        fetchEndpoint: '/api/kitchen/orders/filter',
        updateEndpoint: '/api/kitchen/orders/update',
        acceptStatus: 'ACCEPTED_ORDER',
        rejectStatus: 'CANCELLED',
        fetchParams: { status: 'PENDING', pageSize: 50, pageNumber: 0 }
      };
    case 'cashier':
    case 'captain':
      return {
        fetchEndpoint: '/api/cashier/orders/filter',
        updateEndpoint: '/api/cashier/orders/update',
        acceptStatus: 'CONFIRMED',
        rejectStatus: 'CANCELLED',
        fetchParams: { status: 'PENDING', pageSize: 50, pageNumber: 0 }
      };
    case 'admin':
    case 'supadmin':
      return {
        fetchEndpoint: '/api/admin/orders/filter',
        updateEndpoint: '/api/admin/orders/update',
        acceptStatus: 'CONFIRMED',
        rejectStatus: 'CANCELLED',
        fetchParams: { status: 'PENDING', pageSize: 50, pageNumber: 0 }
      };
    case 'branch':
      return {
        fetchEndpoint: '/api/branch/orders/filter',
        updateEndpoint: '/api/branch/orders/update',
        acceptStatus: 'CONFIRMED',
        rejectStatus: 'CANCELLED',
        fetchParams: { status: 'PENDING', pageSize: 50, pageNumber: 0 }
      };
    default:
      return null;
  }
};

// Statuses representing an order that is still legitimately occupying a table
// (i.e. previously-accepted, currently being prepared/served, not yet closed).
// Used to detect that a new PENDING dine-in order is actually a "Repeated Order"
// — additional items requested from a table that already has an active order.
const ACTIVE_ORDER_STATUSES = new Set([
  'CONFIRMED', 'ACCEPTED', 'ACCEPTED_ORDER',
  'PREPARING', 'PREPARING_ORDER',
  'READY', 'READY_FOR_ORDER',
  'SERVED', 'OUT_FOR_DELIVERY'
]);

const DINING_TYPES = new Set(['DINING', 'DINE_IN']);

const extractTableNumber = (o) => {
  if (!o) return '';
  if (o.tableNumber) return String(o.tableNumber);
  if (o.table_booking_id?.tableNumber) return String(o.table_booking_id.tableNumber);
  if (o.tableBookingId?.tableNumber) return String(o.tableBookingId.tableNumber);
  if (o.tableBookingId?.tableId?.tableNumber) return String(o.tableBookingId.tableId.tableNumber);
  return '';
};

export const OrderAlertProvider = ({ children }) => {
  const [pendingOrders, setPendingOrders] = useState([]);
  const [activeItemsByTable, setActiveItemsByTable] = useState({});
  const [isRinging, setIsRinging] = useState(false);
  const [actionLoading, setActionLoading] = useState(null);
  const [userRole, setUserRole] = useState('');
  const [audioUnlocked, setAudioUnlocked] = useState(false);

  const audioContextRef = useRef(null);
  const soundIntervalRef = useRef(null);
  const pollIntervalRef = useRef(null);
  const audioElementRef = useRef(null);
  const lastSeenOrderIdRef = useRef(0);
  const initialPollRef = useRef(true);

  // Detect user role
  useEffect(() => {
    const role = localStorage.getItem('UserRole') || '';
    setUserRole(role);

    const handleStorage = () => {
      const newRole = localStorage.getItem('UserRole') || '';
      setUserRole(newRole);
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  // ─── Unlock audio on first user interaction (browser autoplay policy) ───
  useEffect(() => {
    const unlockAudio = async () => {
      // Create or resume AudioContext
      try {
        const AudioCtx = window.AudioContext || window.webkitAudioContext;
        if (AudioCtx) {
          if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
            audioContextRef.current = new AudioCtx();
          }
          if (audioContextRef.current.state === 'suspended') {
            await audioContextRef.current.resume();
          }
        }
      } catch (e) { /* ignore */ }

      console.log('[OrderAlert] Audio unlocked by user interaction');
      setAudioUnlocked(true);
      // Remove listeners after first interaction
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };

    document.addEventListener('click', unlockAudio);
    document.addEventListener('touchstart', unlockAudio);
    document.addEventListener('keydown', unlockAudio);

    return () => {
      document.removeEventListener('click', unlockAudio);
      document.removeEventListener('touchstart', unlockAudio);
      document.removeEventListener('keydown', unlockAudio);
    };
  }, []);

  const ringtoneUrl = `${process.env.PUBLIC_URL || ''}/old_phone_ring_tone.mp3`;

  // ─── Play ringtone sound ───
  // Note: On native, we use kitchenAlertService.syncWithPendingOrders() instead
  const playBellSound = useCallback(async () => {
    try {
      // Web fallback - use Audio API (native handled by syncWithPendingOrders)
      if (!Capacitor.isNativePlatform()) {
        if (!audioElementRef.current) {
          audioElementRef.current = new Audio(ringtoneUrl);
          audioElementRef.current.preload = 'auto';
          audioElementRef.current.loop = false;
        }

        const audio = audioElementRef.current;
        audio.volume = 1;
        audio.currentTime = 0;
        const playPromise = audio.play();
        if (playPromise?.catch) {
          playPromise.catch(() => {});
        }
      }
    } catch (err) {
      console.warn('Could not play bell sound:', err);
    }
  }, [ringtoneUrl]);

  // Start ringing
  const startRinging = useCallback(() => {
    // Clear any existing interval first to allow restart
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
    }
    setIsRinging(true);
    playBellSound();
    soundIntervalRef.current = setInterval(() => {
      playBellSound();
    }, 3000); // Ring every 3 seconds
  }, [playBellSound]);

  // Stop ringing
  const stopRinging = useCallback(() => {
    setIsRinging(false);
    if (soundIntervalRef.current) {
      clearInterval(soundIntervalRef.current);
      soundIntervalRef.current = null;
    }
    if (audioElementRef.current) {
      audioElementRef.current.pause();
      audioElementRef.current.currentTime = 0;
    }
  }, []);

  // ─── Poll for pending orders ───
  const fetchPendingOrders = useCallback(async () => {
    const config = getRoleConfig(userRole);
    if (!config) return;

    try {
      // Don't restrict by date — fetch ALL pending orders regardless of when they were created
      const params = { ...config.fetchParams };

      // Run pending fetch in parallel with a wider fetch of recent (non-PENDING)
      // orders so we can detect repeat dine-in orders for tables that already
      // have an active order in progress.
      const [pendingRes, recentRes] = await Promise.all([
        ApiGet(config.fetchEndpoint, params),
        ApiGet(config.fetchEndpoint, { pageSize: 50, pageNumber: 0 }).catch(() => null)
      ]);

      if (pendingRes?.success) {
        const data = pendingRes.success.data?.data;
        const records = data?.records || [];
        setPendingOrders(records);

        // On native, sync with kitchen alert service for background audio
        if (Capacitor.isNativePlatform()) {
          await kitchenAlertService.syncWithPendingOrders(records);
        }
      }

      // Notify order lists to refetch when a newer order appears than the last
      // one we've seen across either pending or recent polls. This keeps
      // OrderList pages in sync without a manual page refresh.
      const pendingRecords = pendingRes?.success?.data?.data?.records || [];
      const recentAll = recentRes?.success?.data?.data?.records || [];
      const maxIdAcrossPolls = [...pendingRecords, ...recentAll].reduce(
        (m, o) => Math.max(m, Number(o?.id) || 0),
        0,
      );
      if (initialPollRef.current) {
        lastSeenOrderIdRef.current = maxIdAcrossPolls;
        initialPollRef.current = false;
      } else if (maxIdAcrossPolls > lastSeenOrderIdRef.current) {
        lastSeenOrderIdRef.current = maxIdAcrossPolls;
        window.dispatchEvent(new CustomEvent('rms:orders-updated', { detail: { orderId: maxIdAcrossPolls, source: 'poll' } }));
      }

      // Build a map: tableNumber → set of in-progress menu item names so the
      // popup can hide items already being prepared/served at that table.
      const map = {};
      const recentRecords = recentRes?.success?.data?.data?.records || [];
      for (const o of recentRecords) {
        if (!DINING_TYPES.has(String(o?.orderType || '').toUpperCase())) continue;
        const status = String(o?.status || '').toUpperCase();
        if (!ACTIVE_ORDER_STATUSES.has(status)) continue;
        const tableNum = extractTableNumber(o);
        if (!tableNum) continue;
        if (!map[tableNum]) map[tableNum] = new Set();
        for (const item of (o.orderItems || [])) {
          const itemStatus = String(item?.status || '').toUpperCase();
          if (itemStatus === 'CANCELLED') continue;
          if (item?.menuItemName) map[tableNum].add(item.menuItemName);
        }
      }
      setActiveItemsByTable(map);
    } catch (error) {
      console.warn('Order alert poll failed:', error);
    }
  }, [userRole]);

  // ─── Manage ringing based on pending orders + audio unlock ───
  useEffect(() => {
    // On native, audio is handled entirely by KitchenAlertService via background polling
    // Don't interfere with the native service from JavaScript
    if (Capacitor.isNativePlatform()) {
      // Just update the UI state
      setIsRinging(pendingOrders.length > 0);
    } else {
      // Web: use Audio API
      if (pendingOrders.length > 0 && audioUnlocked) {
        startRinging();
      } else if (pendingOrders.length === 0) {
        stopRinging();
      }
    }
  }, [pendingOrders.length, audioUnlocked, startRinging, stopRinging]);

  // ─── Start/Stop polling based on role ───
  useEffect(() => {
    if (!ALERT_ROLES.includes(userRole)) {
      stopRinging();
      setPendingOrders([]);
      // Don't stop native polling here - only stop when user explicitly logs out
      return;
    }

    // Reset dedup refs so the first poll in this session baselines
    // without emitting a spurious 'orders-updated' event.
    lastSeenOrderIdRef.current = 0;
    initialPollRef.current = true;

    // On native, start background polling service with a delay
    // to ensure the auth token is available after login
    if (Capacitor.isNativePlatform()) {
      const startNativePolling = () => {
        const authToken = localStorage.getItem('authToken');
        console.log('Checking auth token for native polling:', authToken ? 'found' : 'not found', 'role:', userRole);
        if (authToken) {
          console.log('Starting native polling with role:', userRole, 'token length:', authToken.length);
          kitchenAlertService.startPolling(authToken, userRole);
        } else {
          // Retry after 2 seconds if token not yet available
          console.log('Auth token not found, retrying in 2s...');
          setTimeout(startNativePolling, 2000);
        }
      };
      setTimeout(startNativePolling, 1500);
    }

    // Initial fetch (foreground)
    fetchPendingOrders();

    // Poll every 10 seconds (foreground only - background handled by native service)
    pollIntervalRef.current = setInterval(fetchPendingOrders, 10000);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [userRole, fetchPendingOrders, stopRinging]);

  // ─── Cleanup on unmount ───
  useEffect(() => {
    return () => {
      stopRinging();
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
      }
      if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
        audioContextRef.current.close();
      }
    };
  }, [stopRinging]);

  // ─── Accept order ───
  const acceptOrder = useCallback(async (orderId) => {
    const config = getRoleConfig(userRole);
    if (!config) return;

    setActionLoading(orderId);
    try {
      const payload = { id: orderId, status: config.acceptStatus };
      const response = await ApiPut(config.updateEndpoint, payload);

      if (response.success) {
        toast.success('Order accepted successfully');
        setPendingOrders(prev => prev.filter(o => o.id !== orderId));
        // Stop native alert for this order
        if (Capacitor.isNativePlatform()) {
          kitchenAlertService.stopAlert(orderId);
        }
        window.dispatchEvent(new CustomEvent('rms:orders-updated', { detail: { orderId } }));
      } else {
        toast.error(response.fail || 'Failed to accept order');
      }
    } catch (error) {
      toast.error('Error accepting order');
    } finally {
      setActionLoading(null);
    }
  }, [userRole]);

  // ─── Reject order ───
  const rejectOrder = useCallback(async (orderId) => {
    const config = getRoleConfig(userRole);
    if (!config) return;

    setActionLoading(orderId);
    try {
      const payload = { id: orderId, status: config.rejectStatus };
      const response = await ApiPut(config.updateEndpoint, payload);

      if (response.success) {
        toast.info('Order rejected');
        setPendingOrders(prev => prev.filter(o => o.id !== orderId));
        // Stop native alert for this order
        if (Capacitor.isNativePlatform()) {
          kitchenAlertService.stopAlert(orderId);
        }
      } else {
        toast.error(response.fail || 'Failed to reject order');
      }
    } catch (error) {
      toast.error('Error rejecting order');
    } finally {
      setActionLoading(null);
    }
  }, [userRole]);

  return (
    <OrderAlertContext.Provider value={{
      pendingOrders,
      activeItemsByTable,
      isRinging,
      acceptOrder,
      rejectOrder,
      actionLoading
    }}>
      {children}
    </OrderAlertContext.Provider>
  );
};

export default OrderAlertContext;
